import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Breadcrumbs,
  Link,
  Container,
  Avatar,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  BeachAccess as BeachAccessIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Security as SecurityIcon,
  SwapHoriz as SwapHorizIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

import { Iconify } from 'src/components/iconify';
import { useRouter } from 'src/routes/hooks';
import { getEmployeeById, type EmployeeDto, EmployeeStatus } from 'src/api/api_data';
import { positionLabelTr } from 'src/utils/labels';
import EmployeePersonalInfoTab from '../components/EmployeePersonalInfoTab';
import EmployeeAttendanceTab from '../components/EmployeeAttendanceTab';
import EmployeeLeaveTab from '../components/EmployeeLeaveTab';
import EmployeeDeviceTab from '../components/EmployeeDeviceTab';
import SuspiciousLoginTab from '../components/SuspiciousLoginTab';
import DeviceChangeRequestTab from '../components/DeviceChangeRequestTab';
import EmployeeShiftTab from '../components/EmployeeShiftTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export function EmployeeDetailView() {
  const { userId } = useParams<{ userId: string }>();
  const router = useRouter();

  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!userId) {
        setError('Çalışan ID bulunamadı');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const employeeData = await getEmployeeById(userId);
        setEmployee(employeeData);
      } catch (err: any) {
        setError(err.message || 'Çalışan bilgileri yüklenirken bir hata oluştu');
        setEmployee(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [userId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const getStatusColor = (status: EmployeeStatus) => {
    switch (status) {
      case EmployeeStatus.ACTIVE:
        return 'success';
      case EmployeeStatus.INACTIVE:
        return 'default';
      case EmployeeStatus.ON_LEAVE:
        return 'warning';
      case EmployeeStatus.BANNED:
        return 'error';
      case EmployeeStatus.REQUIRES_VERIFICATION:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: EmployeeStatus) => {
    switch (status) {
      case EmployeeStatus.ACTIVE:
        return 'Aktif';
      case EmployeeStatus.INACTIVE:
        return 'Pasif';
      case EmployeeStatus.ON_LEAVE:
        return 'İzinli';
      case EmployeeStatus.BANNED:
        return 'Yasaklı';
      case EmployeeStatus.REQUIRES_VERIFICATION:
        return 'Doğrulama Bekliyor';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !employee) {
    return (
      <Container maxWidth={false}>
        <Box mt={5}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Çalışan bulunamadı'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => router.back()}
          >
            Çalışanlara Dön
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      {/* Breadcrumbs */}
      <Box mb={{ xs: 2, sm: 3 }}>
        <Breadcrumbs>
          <Link
            color="inherit"
            href="/dashboard"
            onClick={(e) => {
              e.preventDefault();
              router.push('/dashboard');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Ana Sayfa
          </Link>
          <Link
            color="inherit"
            href="/dashboard/employee"
            onClick={(e) => {
              e.preventDefault();
              router.back();
            }}
            sx={{ cursor: 'pointer' }}
          >
            Çalışanlar
          </Link>
          <Typography color="text.primary">
            {employee.firstName} {employee.lastName}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Employee Header Card */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md="auto">
            <Avatar
              sx={{
                width: 120,
                height: 120,
                fontSize: '3rem',
                bgcolor: 'primary.main'
              }}
            >
              {employee.firstName?.[0]}{employee.lastName?.[0]}
            </Avatar>
          </Grid>

          <Grid item xs={12} md>
            <Stack spacing={1.5}>
              <Typography variant="h3" component="h1">
                {employee.firstName} {employee.lastName}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={getStatusLabel(employee.status)}
                  color={getStatusColor(employee.status)}
                  size="small"
                />
                {employee.verified && (
                  <Chip
                    icon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                    label="Doğrulanmış"
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
                {employee.deviceId && (
                  <Chip
                    icon={<PhoneAndroidIcon />}
                    label="Cihaz Kayıtlı"
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Pozisyon
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {positionLabelTr(employee.position) || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Şube
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {employee.branchName || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {employee.email}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Çalışma Saati
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {employee.totalWorkHours?.toFixed(1) || '0'} saat
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Devam Sayısı
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {employee.attendanceCount || 0} gün
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Son Check-in
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {employee.lastCheckInTime
                      ? new Date(employee.lastCheckInTime).toLocaleString('tr-TR')
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12} md="auto">
            <Stack spacing={1}>
              <Button
                variant="contained"
                startIcon={<Iconify icon="solar:pen-bold" />}
                onClick={() => router.push(`/dashboard/employee/${employee.id}/edit`)}
                fullWidth
              >
                Düzenle
              </Button>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:arrow-back-fill" />}
                onClick={() => router.back()}
                fullWidth
              >
                Geri
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label="Kişisel Bilgiler"
            />
            <Tab
              icon={<AccessTimeIcon />}
              iconPosition="start"
              label="Devam Kayıtları"
            />
            <Tab
              icon={<BeachAccessIcon />}
              iconPosition="start"
              label="İzin Talepleri"
            />
            <Tab
              icon={<PhoneAndroidIcon />}
              iconPosition="start"
              label="Cihaz Bilgisi"
            />
            <Tab
              icon={<SecurityIcon />}
              iconPosition="start"
              label="Şüpheli Giriş Denemeleri"
            />
            <Tab
              icon={<SwapHorizIcon />}
              iconPosition="start"
              label="Cihaz Değişiklik Talepleri"
            />
            <Tab
              icon={<ScheduleIcon />}
              iconPosition="start"
              label="Vardiya"
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <EmployeePersonalInfoTab employee={employee} />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <EmployeeAttendanceTab employeeId={employee.id} />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <EmployeeLeaveTab employeeId={employee.id} />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <EmployeeDeviceTab employee={employee} onDeviceReset={() => window.location.reload()} />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <SuspiciousLoginTab employeeId={employee.id} />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <DeviceChangeRequestTab employeeId={employee.id} onDeviceReset={() => window.location.reload()} />
        </TabPanel>

        <TabPanel value={currentTab} index={6}>
          <EmployeeShiftTab employeeId={employee.id} branchId={employee.branchId} />
        </TabPanel>
      </Card>
    </Container>
  );
}
